import {
	Activity,
	ArrowRight,
	CheckCircle,
	Clock,
	Database,
	Network,
	Shield,
	Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '@/components/nav/NavBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Routes } from '@/routes/routes';

const NAV_ENTRIES = [
	{ type: 'link' as const, to: Routes.DASHBOARD, label: 'Dashboard' },
	{ type: 'link' as const, to: Routes.AML_DASHBOARD, label: 'AML Dashboard' },
	{ type: 'link' as const, to: Routes.AML_ALERTS, label: 'AML Alerts' },
];

type Module = {
	id: string;
	title: string;
	description: string;
	icon: React.ElementType;
	status: 'active' | 'coming-soon';
	route?: string;
	badge: string;
};

const MODULES: Module[] = [
	{
		id: 'aml',
		title: 'AML Screening',
		description:
			'Anti-money laundering screening, alert management, case workflows, and compliance reporting.',
		icon: Shield,
		status: 'active',
		route: Routes.AML_DASHBOARD,
		badge: 'Active',
	},
	{
		id: 'connectra',
		title: 'Connectra',
		description:
			'Connection graph analysis, relationship mapping, and network risk visualization.',
		icon: Network,
		status: 'coming-soon',
		badge: 'Coming soon',
	},
	{
		id: 'users',
		title: 'Users',
		description:
			'User management, role assignments, access control, and audit trails.',
		icon: Users,
		status: 'coming-soon',
		badge: 'Coming soon',
	},
];

const STAT_CARDS = [
	{
		title: 'Total Jobs',
		value: '—',
		description: 'All prototype jobs',
		icon: Database,
		badge: { label: '0 active', variant: 'secondary' as const },
	},
	{
		title: 'Running',
		value: '—',
		description: 'Currently processing',
		icon: Activity,
		badge: { label: 'Live', variant: 'success' as const },
	},
	{
		title: 'Completed',
		value: '—',
		description: 'Last 24 hours',
		icon: CheckCircle,
		badge: { label: 'Today', variant: 'outline' as const },
	},
	{
		title: 'Pending',
		value: '—',
		description: 'Queued for processing',
		icon: Clock,
		badge: { label: 'Queued', variant: 'warning' as const },
	},
];

export function Dashboard() {
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-background">
			<NavBar entries={NAV_ENTRIES} />

			<main className="px-8 py-8">
				<div className="mb-8">
					<h1 className="text-3xl font-semibold text-foreground">
						prototype Dashboard
					</h1>
					<p className="mt-1 text-muted-foreground">
						Monitor your data prototype pipelines and jobs.
					</p>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{STAT_CARDS.map((card) => {
						const Icon = card.icon;
						return (
							<Card key={card.title}>
								<CardHeader>
									<div className="flex items-center justify-between">
										<CardDescription>{card.title}</CardDescription>
										<Icon className="h-4 w-4 text-muted-foreground" />
									</div>
									<CardTitle className="text-2xl">{card.value}</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex items-center justify-between">
										<span className="text-xs text-muted-foreground">
											{card.description}
										</span>
										<Badge variant={card.badge.variant}>
											{card.badge.label}
										</Badge>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>

				<div className="mt-8">
					<Card>
						<CardHeader>
							<CardTitle>Recent Activity</CardTitle>
							<CardDescription>
								Latest prototype jobs and pipeline runs.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<p className="py-8 text-center text-sm text-muted-foreground">
								No recent activity. Jobs will appear here once prototype starts.
							</p>
						</CardContent>
					</Card>
				</div>

				<div className="mt-10">
					<div className="mb-4">
						<h2 className="text-xl font-semibold text-foreground">Modules</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							Select a module to get started.
						</p>
					</div>

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{MODULES.map((mod) => {
							const Icon = mod.icon;
							const isActive = mod.status === 'active';
							return (
								<Card
									key={mod.id}
									className={
										isActive
											? 'transition-shadow hover:shadow-md'
											: 'opacity-60'
									}
								>
									<CardHeader>
										<div className="flex items-start justify-between gap-2">
											<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
												<Icon className="h-5 w-5 text-foreground" />
											</div>
											<Badge variant={isActive ? 'success' : 'secondary'}>
												{mod.badge}
											</Badge>
										</div>
										<CardTitle className="mt-3">{mod.title}</CardTitle>
										<CardDescription>{mod.description}</CardDescription>
									</CardHeader>
									<CardFooter>
										<Button
											className="w-full"
											variant={isActive ? 'default' : 'outline'}
											disabled={!isActive}
											onClick={() =>
												isActive && mod.route && navigate(mod.route)
											}
										>
											{isActive ? (
												<>
													Open module
													<ArrowRight className="ml-2 h-4 w-4" />
												</>
											) : (
												'Coming soon'
											)}
										</Button>
									</CardFooter>
								</Card>
							);
						})}
					</div>
				</div>
			</main>
		</div>
	);
}
